import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const ProductImages = ({ 
  images, 
  activeImage, 
  onImageChange,
  product,
  selectedVariant
}) => {
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const imageContainerRef = useRef(null);
  const thumbnailsRef = useRef(null);
  
  // Combine product images and variant images in a smart way
  const getAllImages = () => {
    let allImages = [];
    
    // Add selected variant images first if they exist
    if (selectedVariant && selectedVariant.imageUrl && selectedVariant.imageUrl.length > 0) {
      allImages = [...selectedVariant.imageUrl];
    }
    
    // Then add product images that aren't already included (avoiding duplicates)
    if (images && images.length > 0) {
      images.forEach(img => {
        if (!allImages.includes(img)) {
          allImages.push(img);
        }
      });
    }
    
    // If no images at all, use placeholder (same as caisse / POS)
    if (allImages.length === 0) {
      allImages = ['/logo.jpg'];
    }
    
    return allImages;
  };
  
  const imageList = getAllImages();
    
  useEffect(() => {
    // When activeImage changes, update activeIndex
    const index = imageList.findIndex(img => img === activeImage);
    if (index !== -1) {
      setActiveIndex(index);
    } else if (imageList.length > 0) {
      // If activeImage isn't in the list (happens when switching variants), 
      // use the first image in the list and notify parent
      setActiveIndex(0);
      onImageChange(imageList[0]);
    }
  }, [activeImage, imageList, onImageChange]);
    
  const handleImageError = (e) => {
    e.target.src = '';
  };
  
  const handleImageLoad = () => {
    setLoading(false);
  };
  
  const navigateImage = (direction) => {
    const newIndex = (activeIndex + direction + imageList.length) % imageList.length;
    onImageChange(imageList[newIndex]);
    setActiveIndex(newIndex);
  };
  
  const scrollToActiveThumbnail = () => {
    if (thumbnailsRef.current && imageList.length > 5) {
      const thumbnail = thumbnailsRef.current.children[activeIndex];
      if (thumbnail) {
        thumbnail.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }
  };
  
  useEffect(() => {
    scrollToActiveThumbnail();
  }, [activeIndex]);
  
  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.5 } }
  };
  
  // Get variant-specific label if this image belongs to the selected variant
  const getImageLabel = (imageUrl) => {
    if (selectedVariant && 
        selectedVariant.imageUrl && 
        selectedVariant.imageUrl.includes(imageUrl) &&
        product.isVariant) {
      return selectedVariant.name || 'Variante';
    }
    return null;
  };

  return (
    <motion.div 
      initial="hidden"
      animate="visible"
      variants={fadeIn}
      className="product-images"
    >
      {/* Main Image Container */}
      <div 
        ref={imageContainerRef}
        className="main-image-container relative overflow-hidden bg-white rounded-xl h-[500px] flex items-center justify-center shadow-sm border border-gray-100"
      >
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
            <div className="animate-pulse w-12 h-12 border-4 border-[#174540] border-t-transparent rounded-full"></div>
          </div>
        )}
        
        <AnimatePresence mode="wait">
          <motion.img 
            key={activeImage || imageList[0]}
            initial={{ opacity: 0 }}
            animate={{ opacity: loading ? 0 : 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            src={activeImage || imageList[0]} 
            alt={`${product?.name || 'Product'} ${selectedVariant ? `- ${selectedVariant.name}` : ''}`}
            className="main-image object-contain h-full w-full select-none"
            onError={handleImageError}
            onLoad={handleImageLoad}
            draggable="false"
          />
        </AnimatePresence>
        
        {/* Variant label if applicable */}
        {getImageLabel(activeImage || imageList[0]) && (
          <div className="absolute top-4 left-4 bg-[#174540]/80 text-white text-xs font-medium px-3 py-1.5 rounded-full">
            {getImageLabel(activeImage || imageList[0])}
          </div>
        )}
        
        {/* Navigation arrows */}
        {imageList.length > 1 && (
          <>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                navigateImage(-1);
              }}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 shadow-md flex items-center justify-center text-gray-800 hover:bg-white hover:text-[#174540] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#174540]"
              aria-label="Previous image"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                navigateImage(1);
              }}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 shadow-md flex items-center justify-center text-gray-800 hover:bg-white hover:text-[#174540] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#174540]"
              aria-label="Next image"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}
        
        {/* Image counter badge */}
        {imageList.length > 1 && (
          <div className="absolute top-4 right-4 bg-black/60 text-white text-xs font-medium px-2 py-1 rounded-md">
            {activeIndex + 1} / {imageList.length}
          </div>
        )}
      </div>
      
      {/* Custom CSS for hiding scrollbar but allowing scroll */}
      <style jsx>{`
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </motion.div>
  );
};

export default ProductImages;